import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Download, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { imgVariant } from '../utils/imgUrl';

const CatalogsSection = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    loadCatalogs();
  }, []);

  const loadCatalogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalogs/`);
      if (response.ok) {
        const data = await response.json();
        setCatalogs(data);
      }
    } catch (error) {
      console.error('Error loading catalogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading || catalogs.length === 0) {
    return null;
  }

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Unsere Produktkataloge
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Entdecken Sie unsere Kollektionen in übersichtlichen Katalogen
          </p>
        </div>

        {/* Catalogs Carousel */}
        <div className="relative">
          {/* Scroll Buttons */}
          {catalogs.length > 3 && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:bg-gray-100"
                onClick={() => scroll('left')}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:bg-gray-100"
                onClick={() => scroll('right')}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {catalogs.map((catalog) => (
              <div
                key={catalog.id}
                className="flex-shrink-0 w-80 bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all"
              >
                {/* Preview */}
                <div className="relative h-64 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                  {catalog.file_type === 'pdf' ? (
                    <div className="text-center">
                      <FileText className="h-20 w-20 text-purple-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-600">PDF Katalog</p>
                    </div>
                  ) : (
                    <img
                      src={imgVariant(`${process.env.REACT_APP_BACKEND_URL}${catalog.file_url}`, { w: 600 })}
                      alt={catalog.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="text-center"><div class="h-20 w-20 mx-auto mb-3 flex items-center justify-center bg-purple-200 rounded-full"><svg class="h-10 w-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div><p class="text-sm text-gray-600">Bild</p></div>';
                      }}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{catalog.title}</h3>
                  {catalog.description && (
                    <p className="text-sm text-gray-600 mb-4">{catalog.description}</p>
                  )}
                  
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${catalog.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
                  >
                    <Download className="h-4 w-4" />
                    Katalog öffnen
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CatalogsSection;
